import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { Link, useSearchParams } from 'react-router-dom';
import { Car, Search, Star, SlidersHorizontal, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../services/api';
import BannierePublicitaire from '../components/BannierePublicitaire';

const Annonces = () => {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  const [filtres, setFiltres] = useState({
    recherche: '', marque: '', modele: '', prix_min: '', prix_max: '',
    carburant: '', transmission: '', type_vehicule: ''
  });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const recherche = searchParams.get('recherche') || '';
    const marque = searchParams.get('marque') || '';
    const type_vehicule = searchParams.get('type_vehicule') || '';
    const newFiltres = {
      recherche, marque, modele: '', prix_min: '', prix_max: '',
      carburant: '', transmission: '', type_vehicule
    };
    setFiltres(newFiltres);
    setPage(1);
    chargerAnnonces(newFiltres, 1);
    setInitialized(true);
  }, [searchParams]);

  useEffect(() => {
    if (initialized) chargerAnnonces(filtres, page);
  }, [page]);

  const chargerAnnonces = async (params = filtres, p = page) => {
    setLoading(true);
    try {
      const res = await API.get('/annonces', {
        params: {
          recherche: params.recherche || undefined,
          marque: params.marque || undefined,
          modele: params.modele || undefined,
          prix_min: params.prix_min || undefined,
          prix_max: params.prix_max || undefined,
          carburant: params.carburant || undefined,
          transmission: params.transmission || undefined,
          type_vehicule: params.type_vehicule || undefined,
          page: p
        }
      });
      setAnnonces(res.data.annonces);
      if (res.data.total !== undefined) {
        setTotal(res.data.total);
        setTotalPages(Math.ceil(res.data.total / 10) || 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltres = (e) => {
    e.preventDefault();
    setPage(1);
    chargerAnnonces(filtres, 1);
    setFiltresOuverts(false);
  };

  const handleReset = () => {
    const empty = {
      recherche: '', marque: '', modele: '', prix_min: '', prix_max: '',
      carburant: '', transmission: '', type_vehicule: ''
    };
    setFiltres(empty);
    setPage(1);
    chargerAnnonces(empty, 1);
  };

  const nbFiltresActifs = Object.values(filtres).filter(v => v !== '').length;
  const inputClass = "w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 transition";
  const labelClass = "block text-xs font-bold text-gray-500 mb-1";

  const ContenuFiltres = () => (
    <form onSubmit={handleFiltres} className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100 sticky top-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} style={{ color: '#F5A623' }} />
          <h2 className="font-black text-sm text-gray-800">Filtres</h2>
        </div>
        <button type="button" onClick={handleReset} className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1 transition">
          <X size={12} /> Réinitialiser
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Recherche</label>
          <input type="text" placeholder="Marque, modèle, type..." value={filtres.recherche}
            onChange={(e) => setFiltres({ ...filtres, recherche: e.target.value })} className={inputClass} />
        </div>
        <div style={{ height: '1px', background: '#f3f4f6' }} />
        <div>
          <label className={labelClass}>Marque</label>
          <input type="text" placeholder="Ex: Toyota" value={filtres.marque}
            onChange={(e) => setFiltres({ ...filtres, marque: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Modèle</label>
          <input type="text" placeholder="Ex: Hilux" value={filtres.modele}
            onChange={(e) => setFiltres({ ...filtres, modele: e.target.value })} className={inputClass} />
        </div>
        <div style={{ height: '1px', background: '#f3f4f6' }} />
        <div>
          <label className={labelClass}>Prix (FCFA)</label>
          <div className="flex gap-2">
            <input type="number" placeholder="Min" value={filtres.prix_min}
              onChange={(e) => setFiltres({ ...filtres, prix_min: e.target.value })} className={inputClass} />
            <input type="number" placeholder="Max" value={filtres.prix_max}
              onChange={(e) => setFiltres({ ...filtres, prix_max: e.target.value })} className={inputClass} />
          </div>
        </div>
        <div style={{ height: '1px', background: '#f3f4f6' }} />
        <div>
          <label className={labelClass}>Carburant</label>
          <select value={filtres.carburant} onChange={(e) => setFiltres({ ...filtres, carburant: e.target.value })} className={inputClass}>
            <option value="">Tous</option>
            <option value="essence">Essence</option>
            <option value="diesel">Diesel</option>
            <option value="electrique">Électrique</option>
            <option value="hybride">Hybride</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Transmission</label>
          <select value={filtres.transmission} onChange={(e) => setFiltres({ ...filtres, transmission: e.target.value })} className={inputClass}>
            <option value="">Toutes</option>
            <option value="manuelle">Manuelle</option>
            <option value="automatique">Automatique</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Type de véhicule</label>
          <select value={filtres.type_vehicule} onChange={(e) => setFiltres({ ...filtres, type_vehicule: e.target.value })} className={inputClass}>
            <option value="">Tous</option>
            <option value="berline">Berline</option>
            <option value="suv">SUV</option>
            <option value="4x4">4x4</option>
            <option value="pickup">Pick-up</option>
            <option value="utilitaire">Utilitaire</option>
            <option value="engin">Engin</option>
            <option value="citadine">Citadine</option>
            <option value="monospace">Monospace</option>
            <option value="moto">Moto</option>
          </select>
        </div>
        <button type="submit" style={{ background: '#F5A623' }}
          className="w-full text-black rounded-xl py-2.5 font-black text-sm flex items-center justify-center gap-2 hover:opacity-90 transition">
          <Search size={15} /> Filtrer
        </button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-white text-xl font-black mb-1">Toutes les annonces</h1>
            <p style={{ color: '#F5A623' }} className="text-sm">
              {total > 0 ? `${total} véhicule(s) disponible(s)` : `${annonces.length} véhicule(s) disponible(s)`}
            </p>
            {filtres.recherche && (
              <p className="text-gray-400 text-xs mt-1">
                Résultats pour : <span className="text-white font-bold">"{filtres.recherche}"</span>
              </p>
            )}
          </div>
          {/* Bouton Filtres — visible uniquement sur mobile */}
          <button
            onClick={() => setFiltresOuverts(!filtresOuverts)}
            className="md:hidden flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm relative"
            style={{ background: filtresOuverts ? '#F5A623' : 'white', color: filtresOuverts ? 'black' : '#374151' }}
          >
            <SlidersHorizontal size={16} />
            Filtres
            {nbFiltresActifs > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-xs font-black flex items-center justify-center"
                style={{ background: '#ef4444', color: 'white' }}>
                {nbFiltresActifs}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Panneau filtres mobile — rétractable */}
      {filtresOuverts && (
        <div className="md:hidden px-4 pt-4 pb-2">
          {ContenuFiltres()}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">

          {/* Filtres desktop — colonne gauche */}
          <div className="hidden md:block w-64 flex-shrink-0">
            {ContenuFiltres()}
          </div>

          {/* Résultats */}
          <div className="flex-1 min-w-0">
            <BannierePublicitaire emplacement="recherche_top" className="h-20 mb-4" />

            {loading ? (
              <div className="text-center py-12 text-gray-400">Chargement...</div>
            ) : annonces.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Car size={60} className="mx-auto mb-4 text-gray-300" />
                <p className="font-semibold">Aucune annonce trouvée</p>
                <p className="text-sm mt-1">Essayez de modifier vos filtres</p>
              </div>
            ) : (
              <>
                {/* 2 colonnes mobile / 3 colonnes desktop */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {annonces.map(annonce => (
                    <Link key={annonce.id} to={`/annonces/${annonce.id}`}
                      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition group border border-gray-100">
                      <div className="h-32 bg-gray-100 flex items-center justify-center relative overflow-hidden">
                        {annonce.est_mise_en_avant && (
                          <div style={{ background: '#F5A623' }} className="absolute top-2 left-2 text-black text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 z-10">
                            <Star size={9} /> En avant
                          </div>
                        )}
                        {annonce.photo_principale ? (
                          <img src={getImageUrl(annonce.photo_principale)} alt={annonce.titre}
                            className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                        ) : (
                          <Car size={40} className="text-gray-300" />
                        )}
                      </div>
                      <div className="p-2.5">
                        <h3 className="text-gray-900 font-semibold text-xs truncate">{annonce.titre}</h3>
                        <p className="font-black text-sm mt-1 text-gray-900">
                          {parseInt(annonce.prix).toLocaleString('fr-FR')}
                          <span className="text-xs font-normal"> FCFA</span>
                        </p>
                        <div className="flex gap-1 mt-1 text-xs text-gray-500 flex-wrap">
                          <span>{annonce.annee}</span>
                          <span>•</span>
                          <span className="capitalize">{annonce.carburant}</span>
                        </div>
                        <p className="text-gray-400 text-xs mt-0.5 truncate">{annonce.localisation}</p>
                        <p className="text-gray-400 text-xs mt-0.5">Par {annonce.prenom} {annonce.nom}</p>
                        <p className="text-gray-300 text-xs">Réf. JJC-{String(annonce.id).padStart(6, '0')}</p>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-8">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                      style={{ background: page === 1 ? '#f3f4f6' : '#0A0A0A', color: page === 1 ? '#9ca3af' : 'white' }}
                      className="px-4 py-2 rounded-xl font-bold text-sm transition hover:opacity-90 disabled:cursor-not-allowed">
                      ← Précédent
                    </button>
                    <div className="flex gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <button key={p} onClick={() => setPage(p)}
                          style={{ background: p === page ? '#F5A623' : 'white', color: p === page ? '#000' : '#6b7280', border: p === page ? 'none' : '1px solid #e5e7eb' }}
                          className="w-9 h-9 rounded-xl font-bold text-sm hover:opacity-90 transition">
                          {p}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                      style={{ background: page === totalPages ? '#f3f4f6' : '#0A0A0A', color: page === totalPages ? '#9ca3af' : 'white' }}
                      className="px-4 py-2 rounded-xl font-bold text-sm transition hover:opacity-90 disabled:cursor-not-allowed">
                      Suivant →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Annonces;
